/**
 * Enterprise identity: single sign-on and SCIM/CI provisioning.
 *
 * Both panels are visible to every admin and *usable* on the Enterprise plan —
 * the backend enforces the gate; the page's job is to say clearly which tier a
 * control belongs to instead of failing mysteriously.
 *
 * The provisioning token appears exactly once, at mint time. It is stored
 * hashed server-side, so this page can never show it again — the copy field and
 * the warning around it exist because of that.
 */

import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'

import {
  useCreateOrgToken,
  useDomain,
  useOrgTokens,
  useOrganization,
  usePreindexSettings,
  useRevokeOrgToken,
  useSIEMConfig,
  useSSOConfig,
  useSetPreindexSettings,
  useSetSIEM,
  useSetSSO,
  useTestSIEM,
} from '@/shared/api/hooks'
import { AppShell, PageHead } from '@/shared/ui/Chrome'
import {
  Badge,
  Button,
  ErrorNotice,
  Field,
  Notice,
  Panel,
  formatDate,
} from '@/shared/ui/primitives'
import { NoWorkspace, useCurrentOrgId } from './Team'

export function TeamEnterprise() {
  const orgId = useCurrentOrgId()
  if (!orgId) return <NoWorkspace />
  return <EnterprisePage orgId={orgId} />
}

function EnterprisePage({ orgId }: { orgId: string }) {
  const { data: org } = useOrganization(orgId)
  const isEnterprise = org?.plan === 'enterprise'

  if (org && (org.role === 'member' || org.role === 'billing_admin')) {
    return <Navigate to="/team" replace />
  }

  return (
    <AppShell>
      <PageHead
        index="11"
        eyebrow="Workspace"
        title="SSO & provisioning"
        subtitle="Company sign-on, identity provisioning and audit streaming."
      />

      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        {org && !isEnterprise && (
          <Panel title="Archis Enterprise" index="!" tone="signal">
            <div className="stack" style={{ ['--gap' as string]: 'var(--s4)' }}>
              <p className="dim" style={{ margin: 0, maxWidth: '64ch' }}>
                SAML 2.0 / Okta SSO, SCIM automated user provisioning and SIEM audit streaming are exclusive to <strong>Archis Enterprise</strong>.
              </p>
              <p className="dim" style={{ margin: 0, maxWidth: '64ch', fontSize: '0.875rem' }}>
                The sections below preview the schema and integration capabilities. All configuration controls remain disabled on the {org.plan} tier.
              </p>
              <div className="row" style={{ gap: 'var(--s3)', flexWrap: 'wrap' }}>
                <Link to="/pricing#enterprise">
                  <Button variant="primary">Contact Enterprise Sales</Button>
                </Link>
                <Link to="/pricing#matrix">
                  <Button variant="ghost">Compare all tier capabilities →</Button>
                </Link>
              </div>
            </div>
          </Panel>
        )}

        <SSOPanel orgId={orgId} orgName={org?.name ?? ''} isEnterprise={isEnterprise} />
        <ProvisioningPanel orgId={orgId} isEnterprise={isEnterprise} />
        <SIEMPanel orgId={orgId} isEnterprise={isEnterprise} />
        <StoragePanel orgId={orgId} isEnterprise={isEnterprise} />
      </div>
    </AppShell>
  )
}

function SSOPanel({ orgId, orgName, isEnterprise }: { orgId: string; orgName: string; isEnterprise: boolean }) {
  const { data: config } = useSSOConfig(orgId)
  const { data: claim } = useDomain(orgId)
  const save = useSetSSO(orgId)

  const [providerId, setProviderId] = useState('')
  const [required, setRequired] = useState(false)
  const [capture, setCapture] = useState(false)

  // The domain is not a field on this form any more: SSO routes the domain the
  // workspace has *proven* it controls, and the proof lives on the workspace page.
  // Typing a domain here used to be the entire claim, which is the hole this
  // closes — the server refuses anything but the verified name.
  const verifiedDomain = claim?.verified ? claim.name : ''

  // Hydrate the form once the stored config arrives.
  useEffect(() => {
    if (config) {
      setProviderId(config.provider_id)
      setRequired(config.required)
      setCapture(config.capture)
    }
  }, [config])

  return (
    <Panel title="Single sign-on" index="01">
      <form
        className="stack"
        style={{ ['--gap' as string]: 'var(--s4)' }}
        onSubmit={(event) => {
          event.preventDefault()
          if (!isEnterprise) return
          save.mutate({ provider_id: providerId.trim(), domain: verifiedDomain, required, capture })
        }}
      >
        <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
          Archis signs people in through Firebase Identity Platform, which hosts the SAML or
          OIDC exchange with your identity provider. Configure the provider there, then name
          it here — everyone whose email is under your domain gets routed through it on the
          sign-in page.
        </p>

        <div className="grid-2" style={{ gap: 'var(--s4)' }}>
          <Field
            label="Provider ID"
            hint="From Identity Platform: saml.<name> or oidc.<name>."
          >
            <input
              className="input"
              disabled={!isEnterprise}
              value={providerId}
              onChange={(event) => setProviderId(event.target.value)}
              placeholder="saml.acme-okta"
            />
          </Field>
          <Field
            label="Sign-in domain"
            hint="The domain this workspace has verified. People with addresses under it are routed to SSO."
          >
            <input
              className="input"
              value={verifiedDomain}
              readOnly
              placeholder="No verified domain"
            />
          </Field>
        </div>

        {!verifiedDomain && (
          <Notice tone="warn">
            Verify your domain first. Single sign-on routes the domain this workspace has
            proven it controls — <Link to="/team">claim and verify it</Link> on the workspace
            page, then come back.
          </Notice>
        )}

        <label className="row" style={{ gap: 'var(--s2)', cursor: isEnterprise ? 'pointer' : 'default' }}>
          <input
            type="checkbox"
            disabled={!isEnterprise}
            checked={required}
            onChange={(event) => setRequired(event.target.checked)}
          />
          <span>
            Require SSO — members signing in with Google or GitHub are refused.
            <span className="mono-note"> The workspace owner is exempt, so a broken provider id can always be fixed.</span>
          </span>
        </label>

        <label className="row" style={{ gap: 'var(--s2)', cursor: isEnterprise ? 'pointer' : 'default' }}>
          <input
            type="checkbox"
            disabled={!isEnterprise}
            checked={capture}
            onChange={(event) => setCapture(event.target.checked)}
          />
          <span>
            Capture the domain — anyone signing in with a verified address under it joins
            this workspace automatically and cannot create a separate one.
            <span className="mono-note"> Members an admin removed are never re-added.</span>
          </span>
        </label>

        <ErrorNotice error={save.error} />
        {save.isSuccess && <Notice tone="ok">SSO configuration saved.</Notice>}

        <div className="row" style={{ gap: 'var(--s3)' }}>
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            disabled={!isEnterprise || !verifiedDomain}
          >
            Save SSO settings
          </Button>
          {config?.provider_id && (
            <Button
              type="button"
              variant="ghost"
              disabled={!isEnterprise}
              loading={save.isPending}
              onClick={() =>
                save.mutate({ provider_id: '', domain: '', required: false, capture: false })
              }
            >
              Disable SSO
            </Button>
          )}
        </div>
        {orgName && required && providerId && (
          <p className="mono-note" style={{ margin: 0 }}>
            Enforced server-side on every session of {orgName}, not just hidden in the UI.
          </p>
        )}
      </form>
    </Panel>
  )
}

const kindCopy: Record<'scim' | 'ci', { title: string; body: string }> = {
  scim: {
    title: 'SCIM provisioning',
    body:
      'Point Okta or Entra ID at /scim/v2 with this token. The identity provider then owns who exists: ' +
      'people it creates get a seat when one is free, are linked to their account at first SSO sign-in, ' +
      'and deactivating them frees the seat and revokes their sessions.',
  },
  ci: {
    title: 'CI token (not used yet)',
    body:
      'Reserved for a build agent. No chi command reads this token today: every machine builds ' +
      'its own index, and nothing is published from CI for developers to fetch (#179).',
  },
}

function ProvisioningPanel({ orgId, isEnterprise }: { orgId: string; isEnterprise: boolean }) {
  const { data: tokens } = useOrgTokens(orgId)
  const create = useCreateOrgToken(orgId)
  const revoke = useRevokeOrgToken(orgId)
  const [minted, setMinted] = useState<{ kind: string; token: string } | null>(null)

  const liveOf = (kind: string) =>
    tokens?.find((token) => token.kind === kind && !token.revoked_at)

  return (
    <Panel title="Provisioning tokens" index="02">
      <div className="stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
        <ErrorNotice error={create.error} />
        <ErrorNotice error={revoke.error} />

        {minted && (
          <Notice tone="ok">
            <div className="stack ph-no-capture" style={{ ['--gap' as string]: 'var(--s2)' }} data-testid="minted-org-token">
              <strong>Copy this {minted.kind.toUpperCase()} token now — it is shown exactly once.</strong>
              <code className="ph-no-capture" style={{ wordBreak: 'break-all', userSelect: 'all' }}>
                {minted.token}
              </code>
            </div>
          </Notice>
        )}

        {(['scim', 'ci'] as const).map((kind) => {
          const live = liveOf(kind)
          return (
            <div key={kind} className="stack" style={{ ['--gap' as string]: 'var(--s3)' }}>
              <div className="row row--between" style={{ alignItems: 'baseline' }}>
                <h4 style={{ margin: 0 }}>{kindCopy[kind].title}</h4>
                {live ? (
                  <Badge tone="signal" dot>
                    active · {formatDate(live.created_at)}
                  </Badge>
                ) : (
                  <Badge>not configured</Badge>
                )}
              </div>
              <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
                {kindCopy[kind].body}
              </p>
              <div className="row" style={{ gap: 'var(--s3)' }}>
                <Button
                  size="sm"
                  variant={live ? 'default' : 'primary'}
                  disabled={!isEnterprise}
                  loading={create.isPending}
                  onClick={() => {
                    if (!isEnterprise) return
                    create.mutate(kind, {
                      onSuccess: ({ token }) => setMinted({ kind, token }),
                    })
                  }}
                >
                  {live ? 'Rotate token' : 'Generate token'}
                </Button>
                {live && (
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!isEnterprise}
                    loading={revoke.isPending}
                    onClick={() => {
                      if (!isEnterprise) return
                      setMinted(null)
                      revoke.mutate(kind)
                    }}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          )
        })}

        <p className="mono-note" style={{ margin: 0 }}>
          One live token per kind: rotating mints a new secret and kills the old one in the
          same operation. Tokens are stored hashed and can never be shown again.
        </p>
      </div>
    </Panel>
  )
}

function SIEMPanel({ orgId, isEnterprise }: { orgId: string; isEnterprise: boolean }) {
  const { data: config } = useSIEMConfig(orgId)
  const save = useSetSIEM(orgId)
  const test = useTestSIEM(orgId)

  const [endpoint, setEndpoint] = useState('')
  const [authorization, setAuthorization] = useState('')

  useEffect(() => {
    if (config) setEndpoint(config.endpoint)
  }, [config])

  return (
    <Panel title="SIEM audit streaming" index="03">
      <form
        className="stack"
        style={{ ['--gap' as string]: 'var(--s4)' }}
        onSubmit={(event) => {
          event.preventDefault()
          if (!isEnterprise) return
          save.mutate({ endpoint: endpoint.trim(), authorization: authorization.trim() })
        }}
      >
        <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
          Every event in this workspace&apos;s audit log is also delivered, as JSON, to a
          collector you operate — Splunk HEC, Datadog, or any HTTPS endpoint that accepts an
          authenticated POST. The log itself stays the record: streaming is a best-effort
          mirror, and a collector outage never blocks an admin action.
        </p>

        <div className="grid-2" style={{ gap: 'var(--s4)' }}>
          <Field label="Collector endpoint" hint="HTTPS only.">
            <input
              className="input"
              disabled={!isEnterprise}
              value={endpoint}
              onChange={(event) => setEndpoint(event.target.value)}
              placeholder="https://splunk.acme.com/services/collector/event"
            />
          </Field>
          <Field
            label="Authorization header"
            hint={
              config?.authorization_set
                ? 'One is stored. It is never shown again — enter a new value to replace it.'
                : 'The full header value, e.g. "Splunk <hec-token>".'
            }
          >
            <input
              className="input"
              type="password"
              disabled={!isEnterprise}
              value={authorization}
              onChange={(event) => setAuthorization(event.target.value)}
              placeholder={config?.authorization_set ? '••••••••' : 'Splunk 1a2b3c…'}
            />
          </Field>
        </div>

        <ErrorNotice error={save.error} />
        <ErrorNotice error={test.error} />
        {save.isSuccess && <Notice tone="ok">Streaming configuration saved.</Notice>}
        {test.isSuccess && <Notice tone="ok">Test event delivered — the collector answered 2xx.</Notice>}

        <div className="row" style={{ gap: 'var(--s3)' }}>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!isEnterprise}>
            Save streaming settings
          </Button>
          {config?.endpoint && (
            <>
              <Button
                type="button"
                variant="ghost"
                disabled={!isEnterprise}
                loading={test.isPending}
                onClick={() => isEnterprise && test.mutate()}
              >
                Send test event
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={!isEnterprise}
                loading={save.isPending}
                onClick={() => isEnterprise && save.mutate({ endpoint: '', authorization: '' })}
              >
                Disable streaming
              </Button>
            </>
          )}
        </div>
      </form>
    </Panel>
  )
}

function StoragePanel({ orgId, isEnterprise }: { orgId: string; isEnterprise: boolean }) {
  const { data: settings } = usePreindexSettings(orgId)
  const save = useSetPreindexSettings(orgId)

  const [bucket, setBucket] = useState('')

  useEffect(() => {
    if (settings) setBucket(settings.bucket)
  }, [settings])

  return (
    <Panel title="CI cache storage (not used yet)" index="04">
      <form
        className="stack"
        style={{ ['--gap' as string]: 'var(--s4)' }}
        onSubmit={(event) => {
          event.preventDefault()
          if (!isEnterprise) return
          save.mutate({ bucket: bucket.trim() })
        }}
      >
        <p className="dim" style={{ margin: 0, maxWidth: '62ch' }}>
          Reserved for a CI index cache that is not shipped (#179): no chi command publishes
          or fetches an index artifact today, so nothing is written to this bucket. When it
          ships, artifacts will be signed against the Google Cloud Storage bucket you name
          here, and Archis&apos;s signer service account will need <code>objectAdmin</code> on it.
        </p>

        <Field label="Cloud Storage bucket" hint="Leave empty to use Archis-operated storage.">
          <input
            className="input"
            disabled={!isEnterprise}
            value={bucket}
            onChange={(event) => setBucket(event.target.value)}
            placeholder="acme-archis-cache"
          />
        </Field>

        <ErrorNotice error={save.error} />
        {save.isSuccess && <Notice tone="ok">Storage settings saved.</Notice>}

        <div className="row" style={{ gap: 'var(--s3)' }}>
          <Button type="submit" variant="primary" loading={save.isPending} disabled={!isEnterprise}>
            Save storage settings
          </Button>
          {settings?.bucket && (
            <Button
              type="button"
              variant="ghost"
              disabled={!isEnterprise}
              loading={save.isPending}
              onClick={() => isEnterprise && save.mutate({ bucket: '' })}
            >
              Use Archis storage
            </Button>
          )}
        </div>
      </form>
    </Panel>
  )
}
